"use client";

/**
 * Signer-identification disclosure on the /verify surfaces.
 *
 * Renders the `assurance` object of a verification response — HOW each signer
 * was identified — for both the document-level receipt (per-signer entries)
 * and the per-signer scoped view (that signer's factors only).
 *
 * ⚠️ LEGAL: every legally significant string here (`label`, `levelLabel`,
 * `identificationModelLabel`, `identificationDisclaimer`, `authMethodLabel`)
 * is rendered VERBATIM from the server, which gates the regulated wording
 * behind `AES_LEGAL_COPY_APPROVED` / `AES_VERIFIED_COPY_APPROVED`. Never
 * hardcode a fallback for any of them. The only strings this file owns are
 * neutral chrome labels mirroring the certificate's mechanism captions
 * ("Document authenticated", "Liveness confirmed", "Face matched to
 * document"), which enumerate checks rather than claim a conclusion.
 *
 * The green circled checkmark stays reserved for CRYPTOGRAPHIC verification
 * (locked product decision) — identity levels use indigo/blue/slate badges,
 * never the emerald check.
 */

export interface AssuranceFactor {
  type: string;
  method?: string | null;
  status: string;
  deliveredAt?: string;
  satisfiedAt?: string;
  statementVersion?: string;
  statementSha256?: string;
  attempts?: number;
  identifiedBy?: { name: string; email?: string };
  via?: string;
  evidence?: {
    provider?: string;
    document?: { type?: string; issuing_state?: string };
    liveness?: { status?: string; method?: string };
    face_match?: { status?: string; score?: number };
    verified_name?: string;
    name_match?: { method?: string };
    reused_credential?: boolean;
    [k: string]: unknown;
  };
}

export interface AssuranceSigner {
  name: string;
  email?: string;
  level: string;
  levelLabel: string;
  factors: AssuranceFactor[];
  authMethod?: string;
  authMethodLabel?: string;
  note?: string;
}

export interface Assurance {
  level: string;
  label: string;
  identificationModel?: string;
  identificationModelLabel?: string;
  identificationDisclaimer?: string;
  termsVersion?: string;
  /** Present on a per-signer scoped view — factors are that signer's only. */
  scopedToSigner?: boolean;
  /** Document-level receipt: one entry per signer. */
  signers?: AssuranceSigner[];
  /** Scoped view: the anchored signer's factors. */
  factors?: AssuranceFactor[];
  authMethod?: string;
  authMethodLabel?: string;
  note?: string;
}

const LEVEL_BADGE: Record<string, string> = {
  aes_verified: "bg-indigo-50 border-indigo-200 text-indigo-700",
  aes: "bg-blue-50 border-blue-200 text-blue-700",
  simple: "bg-slate-100 border-slate-200 text-slate-600",
};

/** Neutral names for factor types that did NOT complete — mechanism, no claim. */
const FACTOR_TYPE_NAME: Record<string, string> = {
  sender_ra: "Sender identification",
  oob_code: "One-time code",
  signer_terms: "Signing terms",
  provider_idv: "Identity verification",
  id_document: "ID document check",
  in_person: "In-person check",
};

function fmtDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function titleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The evidence lines for one factor — mirrors the certificate's enumeration. */
function factorLines(f: AssuranceFactor): { label: string; value: string }[] {
  const lines: { label: string; value: string }[] = [];
  if (f.status !== "satisfied") {
    // A factor that did not complete is disclosed, not hidden — evidence
    // either way.
    lines.push({
      label: FACTOR_TYPE_NAME[f.type] || f.type,
      value: `not completed (${f.status})`,
    });
    return lines;
  }
  if (f.type === "provider_idv") {
    const ev = f.evidence || {};
    const provider = ev.provider ? titleCase(String(ev.provider)) : "an independent verifier";
    lines.push({ label: "Identity verified by", value: `${provider} (independent)` });
    const doc = ev.document || {};
    const bits = [doc.type, doc.issuing_state].filter(Boolean);
    if (bits.length) lines.push({ label: "Document authenticated", value: bits.join(", ") });
    const live = ev.liveness || {};
    if (live.status) {
      const m = (live.method || "").toLowerCase();
      lines.push({ label: "Liveness confirmed", value: `${live.status}${m ? ` (${m})` : ""}` });
    }
    const face = ev.face_match || {};
    if (face.status) lines.push({ label: "Face matched to document", value: String(face.status) });
    if (ev.verified_name) lines.push({ label: "Name on document", value: String(ev.verified_name) });
  } else if (f.type === "sender_ra" && f.identifiedBy?.name) {
    const by = f.identifiedBy.email
      ? `${f.identifiedBy.name} (${f.identifiedBy.email})`
      : f.identifiedBy.name;
    lines.push({ label: "Identified by", value: by });
  } else if (f.type === "signer_terms") {
    const at = fmtDate(f.satisfiedAt);
    lines.push({ label: "Signing terms accepted", value: at || "accepted" });
  }
  // oob_code is rendered from the signer-level authMethodLabel (the server's
  // single display copy), not from the raw factor method key.
  return lines;
}

function LevelBadge({ level, label }: { level: string; label: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold ${
        LEVEL_BADGE[level] || LEVEL_BADGE.simple
      }`}
    >
      {label}
    </span>
  );
}

function FactorList({
  factors,
  authMethodLabel,
}: {
  factors: AssuranceFactor[];
  authMethodLabel?: string;
}) {
  const lines = factors.flatMap(factorLines);
  if (authMethodLabel) lines.push({ label: "Authentication", value: authMethodLabel });
  if (!lines.length) return null;
  return (
    <dl className="mt-1.5 space-y-0.5">
      {lines.map((l, i) => (
        <div key={i} className="flex items-baseline gap-1.5 text-xs">
          <dt className="shrink-0 text-slate-500">{l.label}:</dt>
          <dd className="min-w-0 break-words text-slate-800">{l.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function AssuranceSection({ assurance }: { assurance: Assurance }) {
  const scoped = !!assurance.scopedToSigner;
  const signers = assurance.signers || [];
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <LevelBadge level={assurance.level} label={assurance.label} />
      </div>
      {assurance.identificationModelLabel && (
        <p className="mt-1.5 text-sm font-medium text-slate-800">
          {assurance.identificationModelLabel}
        </p>
      )}
      {scoped ? (
        <>
          <FactorList
            factors={assurance.factors || []}
            authMethodLabel={assurance.authMethodLabel}
          />
          {assurance.note && (
            <p className="mt-1.5 text-xs italic text-slate-500">{assurance.note}</p>
          )}
        </>
      ) : (
        signers.length > 0 && (
          <div className="mt-2 space-y-2.5">
            {signers.map((s, i) => (
              <div key={i} className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">{s.name}</span>
                  {s.email && <span className="text-xs text-slate-500">{s.email}</span>}
                  {/* A signer below the document's headline is stated as such —
                      silence would let the headline speak for them. */}
                  {s.level !== assurance.level && (
                    <LevelBadge level={s.level} label={s.levelLabel} />
                  )}
                </div>
                <FactorList factors={s.factors || []} authMethodLabel={s.authMethodLabel} />
                {s.note && <p className="mt-1 text-xs italic text-slate-500">{s.note}</p>}
              </div>
            ))}
          </div>
        )
      )}
      {assurance.identificationDisclaimer && (
        <p className="mt-2 text-[10px] leading-relaxed text-slate-400">
          {assurance.identificationDisclaimer}
        </p>
      )}
    </div>
  );
}
